import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Images, Loader2, Trash2, UploadCloud, HardDrive, AlertTriangle, X, Check, MapPin, ArrowUpDown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import CockpitLayout from "@/components/admin/CockpitLayout";
import LoadMore from "@/components/admin/LoadMore";
import { api } from "@/lib/api";
import { useCursorList } from "@/lib/useCursorList";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472", LINE = "#E7E1D6";

const fmtBytes = (b) => {
  if (!b) return "0 KB";
  if (b >= 1024 * 1024 * 1024) return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
  if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
};

const STATUS = {
  NORMAL: { color: "#1E7E45", bg: "rgba(30,126,69,.10)", label: "Normal" },
  WARNING: { color: "#B7791F", bg: "rgba(216,154,0,.12)", label: "Achtung" },
  CRITICAL: { color: "#C2410C", bg: "rgba(194,65,12,.10)", label: "Kritisch" },
  BLOCKED: { color: "#DC2626", bg: "rgba(220,38,38,.10)", label: "Voll" },
};

export default function BilderGalerie() {
  const [usage, setUsage] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [sort, setSort] = useState("order");
  const [filterLoc, setFilterLoc] = useState("");
  const [locCtx, setLocCtx] = useState(null);
  const [uploadLoc, setUploadLoc] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const dragId = useRef(null);
  const fileRef = useRef(null);

  const loadUsage = useCallback(() => api.get("/usage").then(({ data }) => setUsage(data)).catch(() => {}), []);
  const listParams = useMemo(() => {
    const p = { sort };
    if (filterLoc) p.location_id = filterLoc;
    return p;
  }, [sort, filterLoc]);
  const { items: media, setItems: setMedia, loading, loadingMore, error, hasMore, loadMore, reload } = useCursorList("/media", "media", listParams);

  useEffect(() => {
    api.get("/me/locations").then(({ data }) => {
      if (data.multi) {
        setLocCtx(data);
        setUploadLoc(data.active_location_id || (data.locations?.length ? data.locations[0].id : ""));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  const refresh = async () => { await Promise.all([loadUsage(), reload()]); setSel(new Set()); };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name));
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (locCtx?.multi && uploadLoc) fd.append("location_id", uploadLoc);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data.original_bytes && data.processed_bytes) {
          const saved = Math.max(0, Math.round((1 - data.processed_bytes / data.original_bytes) * 100));
          toast.success(`Optimiert und gespeichert · Original ${fmtBytes(data.original_bytes)} → FUNDIO ${fmtBytes(data.processed_bytes)} · −${saved}%`);
        } else {
          toast.success("Optimiert und gespeichert");
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Bild konnte nicht hochgeladen werden.");
      }
    }
    setUploading(false);
    await refresh();
  };

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSel(sel.size === media.length ? new Set() : new Set(media.map((m) => m.id)));

  const deleteOne = async (id) => {
    setBusy(true);
    try { await api.delete(`/media/${id}`); toast.success("Bild gelöscht. Speicher freigegeben."); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen."); }
    finally { setBusy(false); }
  };

  const deleteSelected = async () => {
    if (!sel.size) return;
    setBusy(true);
    try {
      const { data } = await api.post("/media/bulk-delete", { ids: [...sel] });
      toast.success(`${data.deleted} Medien gelöscht · ${fmtBytes(data.freed_bytes)} freigegeben.`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen."); }
    finally { setBusy(false); }
  };

  const onDrop = async (targetId) => {
    const from = dragId.current; dragId.current = null;
    if (!from || from === targetId) return;
    const ids = media.map((m) => m.id);
    const a = ids.indexOf(from), b = ids.indexOf(targetId);
    if (a < 0 || b < 0) return;
    ids.splice(b, 0, ids.splice(a, 1)[0]);
    setMedia(ids.map((id) => media.find((m) => m.id === id)));
    try { await api.put("/media/reorder", { ordered_ids: ids }); } catch { /* ignore */ }
  };

  const requestUpgrade = async (addon) => {
    try { const { data } = await api.post("/storage/upgrade-request", { addon }); toast.success(data.message); setUpgradeOpen(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Anfrage fehlgeschlagen."); }
  };

  const st = STATUS[usage?.storage?.status] || STATUS.NORMAL;
  const pct = Math.min(100, usage?.storage?.percent || 0);
  const showUpgrade = usage && ["WARNING", "CRITICAL", "BLOCKED"].includes(usage.storage.status);

  return (
    <CockpitLayout active="galerie" title="Bilder & Galerie">
      <div className="fundio-page-stack" data-testid="gold-galerie">
        <div className="fundio-section-head">
          <div>
            <h1 className="fundio-page-title flex items-center gap-2.5" style={{ color: INK }} data-testid="gallery-title">
              <Images size={28} style={{ color: GOLD }} /> Bilder & Galerie
            </h1>
            <p className="mt-1 fundio-muted text-[14px]">Verwalte deine Medien – hochladen, sortieren, löschen. Löschen gibt Speicher sofort frei.</p>
          </div>
          <button data-testid="gallery-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="fundio-action fundio-action--gold">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={16} />} Bilder hochladen
          </button>
          <input ref={fileRef} type="file" accept="image/*,.heic,.heif" multiple hidden onChange={(e) => handleFiles(e.target.files)} data-testid="gallery-upload-input" />
        </div>

        {/* storage header */}
        {usage && (
          <div className="fundio-panel" data-testid="storage-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-[15px] font-bold" style={{ color: INK }}><HardDrive size={16} style={{ color: GOLD }} /> Speicher</h2>
              <span className="rounded-full px-2.5 py-1 text-[11px] font-bold uppercase" style={{ background: st.bg, color: st.color }} data-testid="storage-status">{st.label}</span>
            </div>
            <div className="mt-2 flex items-end justify-between">
              <p className="text-[14px] font-semibold" style={{ color: INK }} data-testid="storage-used">{fmtBytes(usage.storage.used_bytes)} von {fmtBytes(usage.storage.quota_bytes)}</p>
              <p className="text-[18px] font-black tabular-nums" style={{ color: st.color }} data-testid="storage-percent">{pct}%</p>
            </div>
            <div className="mt-2 h-2.5 overflow-hidden rounded-full" style={{ background: "#EFEDE6" }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: st.color }} />
            </div>
            {usage.storage.status === "WARNING" && <p className="mt-3 flex items-center gap-1.5 text-[13px]" style={{ color: "#B7791F" }} data-testid="storage-hint"><AlertTriangle size={14} /> Ihr FUNDIO-Speicher ist zu {pct} % belegt.</p>}
            {["CRITICAL", "BLOCKED"].includes(usage.storage.status) && <p className="mt-3 flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: st.color }} data-testid="storage-hint"><AlertTriangle size={14} /> Ihr Speicher ist {usage.storage.status === "BLOCKED" ? "ausgeschöpft. Neue Uploads sind blockiert." : "fast voll."} Löschen Sie nicht mehr benötigte Medien oder erweitern Sie Ihren Speicher.</p>}
            {showUpgrade && <button data-testid="upgrade-btn" onClick={() => setUpgradeOpen(true)} className="fundio-action mt-3" style={{ minHeight: 34, padding: "6px 12px", fontSize: 13, borderColor: GOLD, color: "var(--f-gold-text)" }}>Speicher erweitern</button>}
          </div>
        )}

        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="select-all-btn" onClick={selectAll} className="fundio-action" style={{ minHeight: 38, padding: "6px 12px", fontSize: 13, color: MUTED }}>
            {sel.size === media.length && media.length ? "Auswahl aufheben" : "Alle auswählen"}
          </button>
          <button data-testid="delete-selected-btn" onClick={deleteSelected} disabled={!sel.size || busy} className="fundio-action" style={{ minHeight: 38, padding: "6px 12px", fontSize: 13, background: "#DC2626", borderColor: "#DC2626", color: "#fff" }}>
            <Trash2 size={14} /> Auswahl löschen{sel.size ? ` (${sel.size})` : ""}
          </button>
          <div className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5" style={{ borderColor: LINE }}>
            <ArrowUpDown size={13} style={{ color: SLATE }} />
            <select data-testid="gallery-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-transparent text-[13px] outline-none" style={{ color: INK }}>
              <option value="order">Reihenfolge</option>
              <option value="date">Neueste zuerst</option>
              <option value="size">Grösste zuerst</option>
            </select>
          </div>
          {locCtx?.multi && (
            <select data-testid="gallery-filter-select" value={filterLoc} onChange={(e) => setFilterLoc(e.target.value)} className="rounded-lg border bg-white px-2.5 py-1.5 text-[13px] outline-none" style={{ borderColor: LINE, color: INK }}>
              <option value="">Alle Standorte</option>
              {(locCtx.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          {locCtx?.multi && (
            <span className="ml-auto flex items-center gap-1.5 text-[12px]" style={{ color: SLATE }}>
              <MapPin size={13} /> Upload nach:
              <select data-testid="gallery-upload-loc" value={uploadLoc} onChange={(e) => setUploadLoc(e.target.value)} className="rounded-md border bg-white px-2 py-1 text-[12px]" style={{ borderColor: LINE }}>
                {(locCtx.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </span>
          )}
        </div>

        {/* grid */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>
        ) : media.length === 0 ? (
          <div className="fundio-panel py-16 text-center" style={{ borderStyle: "dashed", color: SLATE }} data-testid="gallery-empty">
            <Images size={30} className="mx-auto mb-2 opacity-40" />
            Noch keine Bilder. Lade dein erstes Bild hoch.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="media-grid">
            {media.map((m) => (
              <div key={m.id} data-testid={`media-card-${m.id}`}
                draggable={sort === "order"} onDragStart={() => { dragId.current = m.id; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(m.id)}
                className={`fundio-panel group relative overflow-hidden ${sel.has(m.id) ? "ring-2 ring-fundio-gold" : ""}`} style={{ padding: 0 }}>
                <button onClick={() => toggle(m.id)} data-testid={`media-select-${m.id}`}
                  className="absolute left-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-md border bg-white/90"
                  style={{ borderColor: sel.has(m.id) ? GOLD : LINE }}>
                  {sel.has(m.id) && <Check size={15} style={{ color: GOLD }} />}
                </button>
                <button onClick={() => deleteOne(m.id)} disabled={busy} data-testid={`media-delete-${m.id}`}
                  className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-md bg-white/90 text-red-600 opacity-0 transition-opacity group-hover:opacity-100">
                  <Trash2 size={14} />
                </button>
                <div className="aspect-square w-full overflow-hidden bg-fundio-subtle">
                  <img src={m.thumb_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="p-2.5">
                  <p className="truncate text-[12px] font-semibold" style={{ color: INK }} title={m.used_in || "Nicht zugeordnet"}>{m.used_in || "Nicht zugeordnet"}</p>
                  <div className="mt-1 flex items-center justify-between text-[11px]" style={{ color: SLATE }}>
                    <span>{fmtBytes(m.processed_bytes)}</span>
                    {m.created_at && <span>{new Date(m.created_at).toLocaleDateString("de-CH")}</span>}
                  </div>
                  {m.location_name && <span className="mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: "rgba(216,154,0,.12)", color: GOLD }} data-testid={`media-location-${m.id}`}><MapPin size={9} /> {m.location_name}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        <LoadMore hasMore={hasMore} loading={loadingMore} error={error} onLoad={loadMore} testid="media-load-more" />
      </div>

      {/* upgrade dialog */}
      {upgradeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="upgrade-dialog" onClick={() => setUpgradeOpen(false)}>
          <div className="fundio-panel w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-[18px] font-black" style={{ color: INK }}><Sparkles size={18} style={{ color: GOLD }} /> Speicher erweitern</h3>
              <button onClick={() => setUpgradeOpen(false)}><X size={18} style={{ color: SLATE }} /></button>
            </div>
            <p className="mt-2 text-[13px]" style={{ color: MUTED }}>Wähle eine Erweiterung. Keine automatische Buchung – wir bestätigen deine Anfrage.</p>
            <div className="mt-4 space-y-3">
              <button data-testid="upgrade-5gb-btn" onClick={() => requestUpgrade("extra_storage_5gb")} className="flex w-full items-center justify-between rounded-xl border p-4 text-left hover:bg-fundio-subtle" style={{ borderColor: LINE }}>
                <span className="font-bold" style={{ color: INK }}>+5 GB Speicher</span>
                <span className="font-semibold" style={{ color: GOLD }}>CHF 5.- / Monat</span>
              </button>
              <button data-testid="upgrade-25gb-btn" onClick={() => requestUpgrade("extra_storage_25gb")} className="flex w-full items-center justify-between rounded-xl border p-4 text-left hover:bg-fundio-subtle" style={{ borderColor: LINE }}>
                <span className="font-bold" style={{ color: INK }}>+25 GB Speicher</span>
                <span className="font-semibold" style={{ color: GOLD }}>CHF 15.- / Monat</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </CockpitLayout>
  );
}
